<script lang="ts" setup>
import { ref, onMounted, onUnmounted } from "vue";
import { GoogleMap, Marker as GoogleMapMarker, InfoWindow as GoogleMapInfoWindow } from "vue3-google-map";
import { useDisplay, useLocale } from "vuetify";

defineProps(
  {
    intro: String,
    description: String,
    shortdescription: String,
    gpsLat: Number,
    gpsLng: Number,
    id: String,
    logo: String,
    title: String,
    mobileTitle: String,
    headerImage: String,
    mobileHeaderImage: String,
    marker_title_ar: String,
    marker_title_en: String,
    website: String,
    showMarker: {
      type: Boolean,
      default: true
    }
  });

const { mobile } = useDisplay();
const { current, t } = useLocale();

const showMap = ref(false);
const showMarkerInfo = ref(true);
const showDescription = ref(true);
const isMobileView = ref(false);
const wipShow = ref(false);

const checkForMobileDisplay = () => {
  isMobileView.value = (
    window.innerWidth <= 1023 &&
    window.innerWidth >= 768
  );
};

onMounted(() => {
  checkForMobileDisplay(); // Initial check
  window.addEventListener('resize', checkForMobileDisplay);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkForMobileDisplay);
});
</script>

<template>
  <div class="relative" :id="id">
    <!-- Image (base layer) -->
    <div>
      <v-img v-if="mobile" :src="`/images/success-companies/${mobileHeaderImage}`"></v-img>
      <v-img v-else :src="`/images/success-companies/${headerImage}`"></v-img>
    </div>
    <!-- Title overlay (top layer) -->
    <div class="absolute inset-0 flex items-center justify-center z-10">
      <h2 v-if="mobile" class="text-white text-4xl font-bold p-4 text-center" 
      style="text-shadow: 3px 3px 10px rgba(0, 0, 0, 0.7)" v-html="mobileTitle"></h2>
      <h2 v-else class="text-white text-4xl font-bold p-4" 
          style="text-shadow: 3px 3px 10px rgba(0, 0, 0, 0.7)">{{ title }}</h2>
    </div>
  </div>
  <div class="
      rounded-sm bg-white my-5
      mx-[5%] xl:max-w-[60%] xl:w-full xl:mx-auto
      flex flex-col xl:flex-row justify-between items-center" :class='{ mobile, "md:flex-row-reverse": current === "ar" }' :id="id">
    <div class="min-w-[280px] xl:py-14 xl:-mt-[40px] mt-[20px]">  <!-- sm:pt-8 -->
      <v-img :src="`/images/success-companies/${logo}`"></v-img>
    </div>
    <div v-if="!showMap" class='
        py-10 md:py-20 px-10 max-md:w-full'>
      <s-expand>
        <div v-show="!mobile || showDescription" class="ms:p-6">
          <h3 data-v-f7f947d9="" class="text-severus-blue text-xl ms:text-center pb-5" v-html="intro"></h3>
          <div v-html="description"></div>
        </div>
      </s-expand>
      <!-- buttons -->
      <div class="
        flex flex-wrap flex-col md:flex-row
        pt-4 max-md:mx-auto w-[70%]
        [&>*]:!mr-0 gap-2 md:[&>*]:px-10">
        <v-btn v-if="mobile && !isMobileView" class="!rounded-sm mt-4" @click="showDescription = !showDescription" color="black" variant="flat">
          {{ showDescription
            ? t("portfolioCompanies.readLess")
            : t("portfolioCompanies.readMore") }}
        </v-btn>
        <v-btn v-if="website" class="
            swipe !rounded-sm text-white px-6
            !bg-gradient-to-r from-severus-blue-light to-severus-blue
            before:bg-black before:rounded-sm" :href="website" rel="noopener noreferrer" target="_blank" variant="flat">
          {{ t("portfolioCompanies.goToWebsite") }}
        </v-btn>
        <v-btn v-if="gpsLat && gpsLng" append-icon="mdi-chevron-right" class="
            swipe !rounded-sm px-6
            before:bg-black before:rounded-sm
            hover:text-white hover:border-black" @click="showMap = !showMap" prepend-icon="mdi-map-marker" variant="outlined">
          <span>{{ t("portfolioCompanies.location") }}</span>
        </v-btn>
      </div>
    </div>
    <google-map v-if="showMap" api-key="AIzaSyAs-KKpt2Ds6c73HxXhjoyoxdrcEFxNe4Q" class="googlemapMarker relative h-[500px] w-full" :center="{ lat: gpsLat, lng: gpsLng }" :zoom="15">
      <google-map-marker v-if="showMarker" @click="showMarkerInfo = !showMarkerInfo" :options="{ position: { lat: gpsLat, lng: gpsLng } }" />

      <google-map-info-window v-if="showMarker && showMarkerInfo" :options="{ position: { lat: gpsLat, lng: gpsLng }, pixelOffset: { width: 0, height: -40 } }">
        <!-- Content inside the info window -->
        <div>
          <p style="margin-bottom: 5px; font-size: 12px;">{{ marker_title_ar }}</p>          
          <p>{{ marker_title_en }}</p>          
          <p class="p-4 md:px-0 googleInfoWindowDescription">{{ shortdescription }}</p>
          <!-- <v-btn class="
            swipe !rounded-sm text-white px-6
            !bg-gradient-to-r from-severus-blue-light to-severus-blue
            before:bg-black before:rounded-sm" :href="website" rel="noopener noreferrer" target="_blank" variant="flat">
            {{ t("portfolioCompanies.goToWebsite") }}</v-btn> -->
        </div>
      </google-map-info-window>

      <v-btn class="bottom-6 left-2.5 !absolute !rounded-sm" @click="showMap = !showMap" color="black">
        Close map
      </v-btn>
    </google-map>
  </div>
  <v-dialog v-model="wipShow">
    <div class="construction">
      <h3>Site currently under construction.</h3>
      <v-btn class="mt-8" @click="wipShow = false" color="black">
        Dismiss
      </v-btn>
    </div>
  </v-dialog>
</template>

<style scoped>
/* Fix relative bug. */
:deep() {

  .v-btn__overlay,
  .v-btn__underlay {
    display: none;
  }
}
</style>
